import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import { ATTEMPT_DERIVED_STATUS, BUTTON_VARIANTS } from "@miplatform/constants";
import { Box, Typography } from "@mui/material";

export const ISSUE_ACTION_TYPES = {
  CLOSEOUT: "CLOSEOUT",
  PREBILL: "PREBILL",
};

const ActionConfig = ({
  orderTemplateName,
  assignmentNumber,
  haveActions,
}) => ({
  [ISSUE_ACTION_TYPES.CLOSEOUT]: {
    message: `${orderTemplateName} ${assignmentNumber} is ready for closeout.`,
    button: `${haveActions ? "Begin " : ""}Closeout`,
  },
  [ISSUE_ACTION_TYPES.PREBILL]: {
    message: `${orderTemplateName} ${assignmentNumber} is ready for prebill.`,
    button: "Begin Prebill",
  },
});

export const IssueActionRequired = ({
  derivedStatus,
  numOfActions,
  orderTemplateName,
  assignmentNumber,
  isActionDisabled,
  onActionClick,
}) => {
  const { classes, theme } = useStyles({ haveActions: numOfActions > 0 });

  if (
    ![
      ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT,
      ATTEMPT_DERIVED_STATUS.PENDING_PREBILL,
      ATTEMPT_DERIVED_STATUS.JOB_COMPLETE,
      ATTEMPT_DERIVED_STATUS.JOB_INCOMPLETE,
    ].includes(derivedStatus)
  ) {
    return null;
  }

  const action =
    derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL
      ? ISSUE_ACTION_TYPES.PREBILL
      : ISSUE_ACTION_TYPES.CLOSEOUT;

  const config = ActionConfig({
    orderTemplateName,
    assignmentNumber,
    haveActions: numOfActions > 0,
  });

  const { message, button } = config[action];

  if (!message) {
    return null;
  }

  return (
    <Box className={classes.beginCloseoutContainer}>
      <Box className={classes.titleLine}>
        <Box className={classes.countContainer}>
          <Typography className={classes.count}>{numOfActions}</Typography>
        </Box>
        <Typography color={theme.palette.common.white}>
          Action{numOfActions > 1 ? "s" : ""} Required
        </Typography>
      </Box>
      <Box className={classes.beginCloseoutBox}>
        <Box className={classes.beginCloseoutTexts}>
          <Typography fontWeight={theme.typography.fontWeightBold}>
            {message}
          </Typography>
          <Typography fontSize={theme.typography.body2.fontSize}>
            Please carefully add information and review as needed.
          </Typography>
        </Box>
        <MiButton
          variant={BUTTON_VARIANTS.CONTAINED}
          title={button}
          disabled={isActionDisabled}
          onClick={onActionClick}
          sx={{ height: 45 }}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()((theme, { haveActions }) => ({
  beginCloseoutTexts: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  beginCloseoutContainer: {
    boxSizing: "content-box",
    backgroundColor: haveActions
      ? theme.palette.error.main
      : theme.palette.green.main,
    borderRadius: 4,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  titleLine: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  countContainer: {
    borderRadius: 50,
    backgroundColor: theme.palette.common.white,
    width: 20,
    height: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  count: {
    ...theme.typography.body2,
    color: haveActions ? theme.palette.error.main : theme.palette.green.main,
  },
  beginCloseoutBox: {
    borderRadius: 4,
    backgroundColor: theme.palette.common.white,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
}));
